import { useCallback, useEffect, useMemo, useState } from 'react';
import { Preferences } from '@capacitor/preferences';

export type ThemePreference = 'system' | 'light' | 'dark';
export type EffectiveTheme = 'light' | 'dark';

export interface ThemePreferenceStorage {
    get: (args: { key: string }) => Promise<{ value: string | null }>;
    set: (args: { key: string; value: string }) => Promise<void>;
}

interface ThemeMediaQueryList {
    matches: boolean;
    addEventListener?: (event: 'change', handler: () => void) => void;
    removeEventListener?: (event: 'change', handler: () => void) => void;
    addListener?: (handler: () => void) => void;
    removeListener?: (handler: () => void) => void;
}

export interface UseThemePreferenceDeps {
    preferences?: ThemePreferenceStorage;
    matchMedia?: (query: string) => ThemeMediaQueryList;
    documentElement?: HTMLElement | null;
}

export interface UseThemePreferenceState {
    preference: ThemePreference;
    effectiveTheme: EffectiveTheme;
    loading: boolean;
    setPreference: (next: ThemePreference) => Promise<void>;
}

const THEME_KEY = 'theme.preference';
const THEME_VALUES: ReadonlySet<string> = new Set(['system', 'light', 'dark']);

function isThemePreference(value: string | null): value is ThemePreference {
    return value !== null && THEME_VALUES.has(value);
}

function localStorageAdapter(): ThemePreferenceStorage | null {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return {
        get: async ({ key }) => ({ value: window.localStorage.getItem(key) }),
        set: async ({ key, value }) => { window.localStorage.setItem(key, value); },
    };
}

export function useThemePreference(deps?: UseThemePreferenceDeps): UseThemePreferenceState {
    const preferences = deps?.preferences;
    const documentElement = deps?.documentElement;
    const matchMedia = deps?.matchMedia;
    const resolved = useMemo(() => ({
        preferences: preferences ?? Preferences,
        documentElement: documentElement
            ?? (typeof document === 'undefined' ? null : document.documentElement),
        matchMedia: matchMedia
            ?? (typeof window === 'undefined' || !window.matchMedia
                ? undefined
                : window.matchMedia.bind(window)),
    }), [documentElement, matchMedia, preferences]);

    const [preference, setPreferenceState] = useState<ThemePreference>('system');
    const [effectiveTheme, setEffectiveTheme] = useState<EffectiveTheme>('light');
    const [loading, setLoading] = useState(true);

    const systemTheme = useCallback((): EffectiveTheme => {
        const media = resolved.matchMedia?.('(prefers-color-scheme: dark)');
        return media?.matches ? 'dark' : 'light';
    }, [resolved]);

    const applyTheme = useCallback((nextPreference: ThemePreference) => {
        const nextEffective = nextPreference === 'system' ? systemTheme() : nextPreference;
        setEffectiveTheme(nextEffective);
        if (resolved.documentElement) {
            resolved.documentElement.dataset.theme = nextEffective;
        }
    }, [resolved, systemTheme]);

    const readStoredPreference = useCallback(async (): Promise<ThemePreference> => {
        try {
            const { value } = await resolved.preferences.get({ key: THEME_KEY });
            if (isThemePreference(value)) return value;
        } catch {
            const fallback = localStorageAdapter();
            if (fallback) {
                const { value } = await fallback.get({ key: THEME_KEY });
                if (isThemePreference(value)) return value;
            }
        }
        return 'system';
    }, [resolved]);

    const writeStoredPreference = useCallback(async (next: ThemePreference) => {
        try {
            await resolved.preferences.set({ key: THEME_KEY, value: next });
            return;
        } catch {
            const fallback = localStorageAdapter();
            if (fallback) await fallback.set({ key: THEME_KEY, value: next });
        }
    }, [resolved]);

    const setPreference = useCallback(async (next: ThemePreference) => {
        setPreferenceState(next);
        applyTheme(next);
        await writeStoredPreference(next);
    }, [applyTheme, writeStoredPreference]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        void readStoredPreference().then((stored) => {
            if (cancelled) return;
            setPreferenceState(stored);
            applyTheme(stored);
            setLoading(false);
        });
        return () => { cancelled = true; };
    }, [applyTheme, readStoredPreference]);

    useEffect(() => {
        const media = resolved.matchMedia?.('(prefers-color-scheme: dark)');
        const refresh = () => { applyTheme(preference); };
        refresh();
        if (!media || preference !== 'system') return undefined;

        if (media.addEventListener && media.removeEventListener) {
            media.addEventListener('change', refresh);
            return () => { media.removeEventListener?.('change', refresh); };
        }
        media.addListener?.(refresh);
        return () => { media.removeListener?.(refresh); };
    }, [applyTheme, preference, resolved]);

    return {
        preference,
        effectiveTheme,
        loading,
        setPreference,
    };
}
